// Education Inquiries Types

export type InquiryStatus =
  | 'new'
  | 'contacted'
  | 'in_discussion'
  | 'quoted'
  | 'won'
  | 'lost'
  | 'unresponsive'
  | 'spam';

export interface InquiryStatusMeta {
  key: InquiryStatus;
  label: string;
  color: string;
}

export const INQUIRY_STATUSES: InquiryStatusMeta[] = [
  { key: 'new', label: 'New', color: '#3b82f6' },
  { key: 'contacted', label: 'Contacted', color: '#8b5cf6' },
  { key: 'in_discussion', label: 'In Discussion', color: '#06b6d4' },
  { key: 'quoted', label: 'Quoted', color: '#f59e0b' },
  { key: 'won', label: 'Won (Converted)', color: '#10b981' },
  { key: 'lost', label: 'Lost', color: '#ef4444' },
  { key: 'unresponsive', label: 'Unresponsive', color: '#6b7280' },
  { key: 'spam', label: 'Spam', color: '#1f2937' },
];

export const INQUIRY_STATUS_MAP: Record<InquiryStatus, InquiryStatusMeta> =
  INQUIRY_STATUSES.reduce((acc, s) => {
    acc[s.key] = s;
    return acc;
  }, {} as Record<InquiryStatus, InquiryStatusMeta>);

export interface EducationInquiry {
  id: string;
  name: string;
  email: string;
  organization: string;
  role: string;
  phone: string;
  estimated_quantity: number | null;
  intended_use: string;
  timeline: string;
  message: string;
  email_sent_successfully: boolean;
  email_send_error: string;
  status: InquiryStatus;
  status_display: string;
  assigned_to: number | null;
  assigned_to_email: string | null;
  assigned_to_name: string | null;
  internal_notes: string;
  contacted_at: string | null;
  converted_at: string | null;
  conversion_value: string | null;
  lost_reason: string;
  created_at: string;
  updated_at: string;
}

export interface InquiryFilters {
  status?: InquiryStatus;
  assigned_to?: number | 'unassigned';
  search?: string;
  created_after?: string;
  created_before?: string;
  intended_use?: string;
  timeline?: string;
  email_sent?: boolean;
  ordering?: InquiryOrdering;
  limit?: number;
  offset?: number;
}

export type InquiryOrdering =
  | '-created_at'
  | 'created_at'
  | '-updated_at'
  | 'updated_at'
  | 'organization'
  | '-organization'
  | 'status'
  | '-status'
  | 'estimated_quantity'
  | '-estimated_quantity';

export interface PaginatedInquiries {
  count: number;
  next_offset: number | null;
  results: EducationInquiry[];
}

export interface InquiryPatchPayload {
  status?: InquiryStatus;
  assigned_to?: number | null;
  internal_notes?: string;
  contacted_at?: string | null;
  converted_at?: string | null;
  conversion_value?: string | null;
  lost_reason?: string;
  estimated_quantity?: number | null;
}

export interface InquiryStatsTimeSeriesPoint {
  date: string;
  count: number;
  won: number;
  estimated_units: number;
}

export interface InquiryTopValue {
  value: string;
  count: number;
}

export interface InquiryStats {
  total_inquiries: number;
  status_counts: Record<InquiryStatus, number>;
  recent_counts: {
    last_7_days: number;
    last_30_days: number;
    last_90_days: number;
  };
  unassigned_count: number;
  email_delivery: {
    sent: number;
    failed: number;
    success_rate: number;
  };
  quantities: {
    total_estimated_units: number;
    won_units: number;
    avg_estimated_units: number | null;
  };
  conversion: {
    won_count: number;
    lost_count: number;
    decided_count: number;
    conversion_rate: number | null;
    total_conversion_value: string | null;
    avg_time_to_contact_seconds: number | null;
    avg_time_to_conversion_seconds: number | null;
  };
  window_days: number;
  time_series_daily: InquiryStatsTimeSeriesPoint[];
  top_intended_uses: InquiryTopValue[];
  top_timelines: InquiryTopValue[];
}

export const STATS_WINDOW_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '365 days' },
] as const;

export const ORDERING_OPTIONS: { value: InquiryOrdering; label: string }[] = [
  { value: '-created_at', label: 'Newest first' },
  { value: 'created_at', label: 'Oldest first' },
  { value: '-updated_at', label: 'Recently updated' },
  { value: 'updated_at', label: 'Stalest update' },
  { value: 'organization', label: 'Organization A–Z' },
  { value: '-organization', label: 'Organization Z–A' },
  { value: 'status', label: 'Status A–Z' },
  { value: '-status', label: 'Status Z–A' },
  { value: '-estimated_quantity', label: 'Largest qty first' },
  { value: 'estimated_quantity', label: 'Smallest qty first' },
];
